import { useReducer } from 'react';

import type { ValidationMessageLevel } from '@/services/bulk-donor-schedule';

import type { BulkDonorScheduleInput } from '../utils/schema';

export type Messages = Record<
  string,
  {
    level: ValidationMessageLevel;
    message: string;
    suggestions?: string[];
  }
>;

export type BulkDonorScheduleState<Row = BulkDonorScheduleInput> = (
  | {
      type: 'idle';
      readonly: false;
      loading: false;
      errors: Record<string, string> | undefined;
      messages: Messages | undefined;
    }
  | {
      type: 'client_validating';
      readonly: true;
      loading: true;
      errors: undefined;
      messages: undefined;
    }
  | {
      type: 'server_validating';
      readonly: true;
      loading: true;
      errors: undefined;
      messages: undefined;
    }
  | {
      type: 'server_validated';
      readonly: false;
      loading: false;
      errors: undefined;
      messages: Messages | undefined;
    }
  | {
      type: 'uploading';
      readonly: true;
      loading: true;
      errors: undefined;
      messages: Messages | undefined;
    }
  | {
      type: 'success';
      readonly: true;
      loading: false;
      errors: undefined;
      messages: Messages | undefined;
    }
) & {
  rows: Row[];
  warningRowIndexes: ReadonlySet<number>;
};

const createInitialState = <Row>(): BulkDonorScheduleState<Row> => ({
  type: 'idle',
  readonly: false,
  loading: false,
  rows: Array.from({ length: 31 }) as Row[],
  errors: undefined,
  messages: undefined,
  warningRowIndexes: new Set(),
});

export const initialState = createInitialState<BulkDonorScheduleInput>();

export type BulkDonorScheduleAction<Row = BulkDonorScheduleInput> =
  | { type: 'reset' }
  | { type: 'idle' }
  | { type: 'add_rows'; count: number }
  | { type: 'set_row'; rowIndex: number; data: Row }
  | { type: 'client_validating' }
  | {
      type: 'client_validated';
      errors: Record<string, string> | undefined;
    }
  | {
      type: 'server_validated';
      errors: Record<string, string> | undefined;
      messages: Messages | undefined;
    }
  | { type: 'uploading' }
  | { type: 'request_cancelled' }
  | { type: 'server_error' }
  | { type: 'success'; warningRowIndexes?: ReadonlySet<number> }
  | { type: 'upload_from_excel'; data: Row[] }
  | { type: 'set_rows'; rows: Row[] };

export function bulkDonorScheduleReducer<Row = BulkDonorScheduleInput>(
  state: BulkDonorScheduleState<Row>,
  action:
    | BulkDonorScheduleAction<Row>
    | ((state: BulkDonorScheduleState<Row>) => BulkDonorScheduleAction<Row>),
): BulkDonorScheduleState<Row> {
  if (typeof action === 'function') {
    action = action(state);
  }

  switch (action.type) {
    case 'reset':
      return createInitialState<Row>();
    case 'idle':
      return {
        type: 'idle',
        readonly: false,
        loading: false,
        rows: state.rows,
        warningRowIndexes: state.warningRowIndexes,
        errors: undefined,
        messages: undefined,
      };
    case 'add_rows':
      return {
        type: 'idle',
        readonly: false,
        loading: false,
        rows: [
          ...state.rows,
          ...(Array.from({ length: action.count }) as Row[]),
        ],
        warningRowIndexes: state.warningRowIndexes,
        errors: undefined,
        messages: undefined,
      };
    case 'set_row': {
      const newRows = [...state.rows];
      newRows[action.rowIndex] = action.data;
      return {
        type: 'idle',
        readonly: false,
        loading: false,
        rows: newRows,
        warningRowIndexes: state.warningRowIndexes,
        errors: undefined,
        messages: undefined,
      };
    }
    case 'client_validating':
      return {
        type: 'client_validating',
        readonly: true,
        loading: true,
        rows: state.rows,
        warningRowIndexes: state.warningRowIndexes,
        errors: undefined,
        messages: undefined,
      };
    case 'client_validated':
      if (action.errors) {
        return {
          type: 'idle',
          readonly: false,
          loading: false,
          rows: state.rows,
          warningRowIndexes: state.warningRowIndexes,
          errors: action.errors,
          messages: undefined,
        };
      }
      return {
        type: 'server_validating',
        readonly: true,
        loading: true,
        rows: state.rows,
        warningRowIndexes: state.warningRowIndexes,
        errors: undefined,
        messages: undefined,
      };
    case 'server_validated':
      if (action.errors) {
        return {
          type: 'idle',
          readonly: false,
          loading: false,
          rows: state.rows,
          warningRowIndexes: state.warningRowIndexes,
          errors: action.errors,
          messages: action.messages,
        };
      }
      return {
        type: 'server_validated',
        readonly: false,
        loading: false,
        rows: state.rows,
        warningRowIndexes: state.warningRowIndexes,
        errors: undefined,
        messages: action.messages,
      };
    case 'uploading':
      return {
        type: 'uploading',
        readonly: true,
        loading: true,
        rows: state.rows,
        warningRowIndexes: state.warningRowIndexes,
        errors: undefined,
        messages:
          state.type === 'server_validated' ? state.messages : undefined,
      };
    case 'server_error':
    case 'request_cancelled':
      return {
        type: 'idle',
        readonly: false,
        loading: false,
        rows: state.rows,
        warningRowIndexes: state.warningRowIndexes,
        errors: undefined,
        messages: undefined,
      };
    case 'success':
      return {
        type: 'success',
        readonly: true,
        loading: false,
        rows: state.rows,
        errors: undefined,
        messages: state.type === 'uploading' ? state.messages : undefined,
        warningRowIndexes: action.warningRowIndexes ?? state.warningRowIndexes,
      };
    case 'upload_from_excel':
      return {
        type: 'idle',
        readonly: false,
        loading: false,
        rows: [...action.data, ...(Array.from({ length: 10 }) as Row[])],
        errors: undefined,
        messages: undefined,
        warningRowIndexes: new Set(),
      };
    case 'set_rows':
      return { ...state, rows: action.rows };
    default:
      return state;
  }
}

export function useBulkDonorScheduleStore<Row = BulkDonorScheduleInput>() {
  return useReducer(bulkDonorScheduleReducer<Row>, createInitialState<Row>());
}
